import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from "react-redux";
import {
  LogoutOutlined,
  ExpandLess,
  ExpandMore,
  DashboardOutlined,
  ReceiptLongOutlined,
  Inventory2Outlined,
  WarehouseOutlined,
  PeopleAltOutlined,
  AssessmentOutlined,
  BadgeOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import { Logout, reset } from '../fitur/AuthSlice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import Button from "@mui/material/Button";
import { useBranding, useDynamicFavicon } from '../hooks/useBranding';
import useSWR from 'swr';
import axios from 'axios';
import { getApiBaseUrl } from './langganan/langgananUtil';

const ADMIN = ['admin', 'superadmin'];
const STAFF = ['karyawan', 'kasir', 'dapur'];

// Urutan dari yang paling sering dipakai. Menu yang hanya punya satu sub menu tampil sebagai link biasa.
// roles = peran yang boleh melihat; when = syarat tambahan; labelFor = nama lain untuk peran tertentu.
const MENU = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardOutlined fontSize="small" />,
    items: [{ label: 'Dashboard', to: '/dashboard' }],
  },
  {
    key: 'transaksi',
    label: 'Transaksi',
    icon: <ReceiptLongOutlined fontSize="small" />,
    items: [
      { label: 'Laporan Transaksi', to: '/transaction', fitur: 'kasir' },
      { label: 'Reservasi', to: '/reservasi', roles: ADMIN, fitur: 'reservasi', when: (u) => u?.role === 'superadmin' || u?.cabang?.tipe_cabang === 'cafe' },
      { label: 'Promo', to: '/promo', roles: ADMIN, fitur: 'promo' },
      { label: 'Buat Pembelian', to: '/createjurnal', roles: ['superadmin'], fitur: 'jurnal' },
    ],
  },
  {
    key: 'produk',
    label: 'Produk',
    icon: <Inventory2Outlined fontSize="small" />,
    items: [
      { label: 'Kategori Produk', to: '/category', fitur: 'produk' },
      { label: 'Produk', to: '/product', fitur: 'produk' },
      { label: 'Cetak Label', to: '/cetak-label', roles: ADMIN, fitur: 'produk' },
      { label: 'Serial Number', to: '/serial-number', fitur: 'serial_number' },
    ],
  },
  {
    key: 'stok',
    label: 'Stok',
    icon: <WarehouseOutlined fontSize="small" />,
    items: [
      { label: 'Stok Produk', to: '/stockcabang', roles: ['admin'], fitur: 'stok' },
      { label: 'Stok Semua Cabang', to: '/stockallbranch', roles: ['superadmin'], fitur: 'stok' },
      { label: 'Riwayat Produk', to: '/mutasi', fitur: 'stok' },
      { label: 'Stok Opname', to: '/stok-opname', roles: ADMIN, fitur: 'stok' },
      { label: 'Bahan Baku & Resep', to: '/bahan-baku', roles: ADMIN, fitur: 'stok' },
      { label: 'Pembelian (PO)', to: '/pembelian', roles: ADMIN, fitur: 'stok' },
      { label: 'Gudang', to: '/wearhouse', roles: ['superadmin'], fitur: 'gudang' },
    ],
  },
  {
    key: 'customer',
    label: 'Customer',
    icon: <PeopleAltOutlined fontSize="small" />,
    items: [
      { label: 'Kategori Customer', to: '/kategoricustomer', fitur: 'customer' },
      { label: 'Data Customer', to: '/customer', fitur: 'customer' },
      { label: 'Piutang Pelanggan', to: '/piutang', roles: ADMIN, fitur: 'kasir' },
    ],
  },
  {
    key: 'laporan',
    label: 'Laporan',
    icon: <AssessmentOutlined fontSize="small" />,
    items: [
      { label: 'Laporan Shift', to: '/laporan-shift', roles: ADMIN, fitur: 'kasir' },
      { label: 'Riwayat Refund', to: '/riwayat-refund', roles: ADMIN, fitur: 'kasir' },
      { label: 'Laporan Piutang', to: '/laporan-piutang', roles: ADMIN, fitur: 'kasir' },
      { label: 'Laporan', to: '/laporan', fitur: 'laporan' },
      { label: 'Penjualan per Kategori', to: '/penjualankategori', fitur: 'laporan' },
      { label: 'Stok', to: '/stockpages', fitur: 'laporan' },
      { label: 'Rekap', to: '/komprehensif', fitur: 'laporan' },
      { label: 'Pengeluaran', to: '/pengeluaran', roles: ADMIN, fitur: 'laporan' },
      { label: 'Laba Rugi', to: '/laba-rugi', roles: ADMIN, fitur: 'laporan' },
      { label: 'Laporan Serial Number', to: '/serial-report', fitur: 'serial_number' },
      { label: 'Jurnal', to: '/getjurnal', roles: ['superadmin'], fitur: 'jurnal' },
    ],
  },
  {
    key: 'karyawan',
    label: 'Karyawan',
    icon: <BadgeOutlined fontSize="small" />,
    items: [
      { label: 'Data User', to: '/userlist', fitur: 'kelola_user' },
      { label: 'Presensi', to: '/presensi', fitur: 'presensi' },
      { label: 'Pengaturan Absensi', to: '/pengaturan-absensi', roles: ADMIN, fitur: 'presensi' },
      { label: 'Gaji Karyawan', to: '/gaji-karyawan', roles: ADMIN, fitur: 'gaji' },
      { label: 'Data Gaji Saya', to: '/gaji-saya', roles: STAFF, fitur: 'gaji' },
      { label: 'Konfirmasi Gaji', to: '/gaji-konfirmasi', roles: STAFF, fitur: 'gaji' },
    ],
  },
  {
    key: 'pengaturan',
    label: 'Pengaturan',
    icon: <SettingsOutlined fontSize="small" />,
    items: [
      { label: 'Konfigurasi Nota & PPN', labelFor: { admin: 'Konfigurasi & PPN' }, to: '/konfigurasi', roles: ADMIN },
      { label: 'Struk', to: '/struk', roles: ADMIN },
      { label: 'Ulasan Google', to: '/ulasangoogle', roles: ADMIN },
      { label: 'Log Aktivitas', to: '/log-aktivitas', roles: ADMIN },
      { label: 'Cabang', to: '/branch', roles: ADMIN },
      // Superadmin memilih cabang cafe di halamannya; admin hanya bila cabangnya cafe.
      { label: 'Data Meja', to: '/meja', roles: ADMIN, fitur: 'menu_qr', when: (u) => u?.role === 'superadmin' || u?.cabang?.tipe_cabang === 'cafe' },
      { label: 'Data Take Away', to: '/takeaway', roles: ADMIN, fitur: 'takeaway' },
      { label: 'Metode Non Cash', to: '/noncashmethod', roles: ADMIN, fitur: 'noncash' },
      // Admin cabang hanya melihatnya bila ada integrasi yang diatur superadmin sebagai "per cabang".
      { label: 'Integrasi', to: '/integrasi', roles: ADMIN, when: (u) => u?.role === 'superadmin' || (u?.integrasiCabang || []).length > 0 },
      { label: 'Paket & Fitur', to: '/paket-fitur', roles: ['superadmin'] },
      { label: 'Pengaturan Homepage', to: '/homepage-settings', roles: ['superadmin'] },
      { label: 'Rilis Aplikasi', to: '/rilis-aplikasi', roles: ['superadmin'] },
      { label: 'Download Aplikasi', to: '/download-aplikasi', roles: ADMIN },
      { label: 'Masukan', to: '/masukan', roles: ['superadmin'] },
      { label: 'Rating Aplikasi', to: '/rating-aplikasi', roles: ['superadmin'] },
    ],
  },
];

// Menu disembunyikan bila fitur terkait tidak ada di paket cabang. Superadmin / tanpa data langganan: tampil semua.
const fiturTersedia = (item, user) =>
  !item.fitur || !user?.langganan || user.langganan.fitur.includes(item.fitur);

export const itemVisible = (item, user) =>
  (!item.roles || (user && item.roles.includes(user.role))) && (!item.when || item.when(user)) && fiturTersedia(item, user);

export const NavItem = ({ item, icon, nested }) => (
  <ListItemButton
    component={NavLink}
    to={item.to}
    sx={{
      textDecoration: 'none',
      color: '#cbd5e1',
      borderRadius: 1,
      py: 0.75,
      px: 1,
      pl: nested ? 2 : 1,
      '&:hover': { bgcolor: '#374151', color: 'white' },
      '&.active': { bgcolor: '#374151', color: 'white' },
    }}
  >
    {icon && <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>{icon}</ListItemIcon>}
    <ListItemText
      primary={item.label}
      primaryTypographyProps={{ fontSize: nested ? 14 : 15, fontWeight: nested ? 400 : 600 }}
    />
  </ListItemButton>
);

// Daftar menu bertingkat (dipakai juga oleh sidebar presensi): hanya yang boleh dilihat peran
// pengguna, grup yang berisi halaman aktif terbuka otomatis.
export const GroupedMenu = ({ menu }) => {
  const location = useLocation();
  const { user: userLogin } = useSelector((state) => state.auth);
  // Daftar integrasi per cabang bisa diubah superadmin kapan saja: admin cabang mengeceknya ulang
  // tiap menit dan saat tab kembali dibuka, supaya menu Integrasi muncul/hilang tanpa memuat ulang halaman.
  const { data: meTerbaru } = useSWR(
    userLogin?.role === 'admin' ? `${getApiBaseUrl()}/me` : null,
    (url) => axios.get(url).then((r) => r.data),
    { refreshInterval: 60000, revalidateOnFocus: true }
  );
  const user = useMemo(
    () => (meTerbaru && userLogin ? { ...userLogin, integrasiCabang: meTerbaru.integrasiCabang || [] } : userLogin),
    [userLogin, meTerbaru]
  );

  const visibleMenu = useMemo(
    () =>
      menu.map((group) => ({
        ...group,
        items: group.items
          .filter((item) => itemVisible(item, user))
          .map((item) => ({ ...item, label: item.labelFor?.[user?.role] || item.label })),
      })).filter((group) => group.items.length > 0),
    [menu, user]
  );

  const activeGroupKey = visibleMenu.find((g) =>
    g.items.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))
  )?.key;
  const [openGroups, setOpenGroups] = useState(() => (activeGroupKey ? { [activeGroupKey]: true } : {}));
  useEffect(() => {
    if (activeGroupKey) {
      setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
    }
  }, [activeGroupKey]);
  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Stack spacing={0.5}>
      {visibleMenu.map((group) =>
        group.items.length === 1 ? (
          <NavItem key={group.key} item={group.items[0]} icon={group.icon} />
        ) : (
          <Box key={group.key}>
            <ListItemButton
              onClick={() => toggleGroup(group.key)}
              sx={{
                color: openGroups[group.key] ? 'white' : '#cbd5e1',
                borderRadius: 1,
                py: 0.75,
                px: 1,
                '&:hover': { bgcolor: '#374151', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>{group.icon}</ListItemIcon>
              <ListItemText primary={group.label} primaryTypographyProps={{ fontSize: 15, fontWeight: 600 }} />
              {openGroups[group.key] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={!!openGroups[group.key]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ ml: 2.5, borderLeft: '1px solid #374151' }}>
                {group.items.map((item) => (
                  <NavItem key={item.to} item={item} nested />
                ))}
              </List>
            </Collapse>
          </Box>
        )
      )}
    </Stack>
  );
};

export const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logoUrl } = useBranding();
  useDynamicFavicon();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleLogoutClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenConfirmDialog(true);
  };

  const handleConfirmLogout = () => {
    dispatch(Logout());
    dispatch(reset());
    navigate("/");
    setOpenConfirmDialog(false);
  };

  const handleCancelLogout = () => {
    setOpenConfirmDialog(false);
  };

  return (
    <Box
      sx={{
        bgcolor: '#1e293b',
        color: 'white',
        display: { xs: 'block', lg: 'flex' },
        flexDirection: 'column',
        height: '100vh',
        width: { xs: 200, lg: 250 },
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        overflow: 'auto',
      }}
    >
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelLogout}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Konfirmasi Keluar
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Apakah Anda yakin ingin keluar dari sistem?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} color="primary">
            Batal
          </Button>
          <Button
            onClick={handleConfirmLogout}
            color="error"
            autoFocus
            startIcon={<LogoutOutlined />}
          >
            Ya, Keluar
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 60,
          p: 2,
          borderBottom: '1px solid #374151',
          position: 'sticky',
          top: 0,
          bgcolor: '#1e293b',
          zIndex: 2,
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt="Logo"
            sx={{ maxHeight: 40, maxWidth: '80%', objectFit: 'contain' }}
          />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Juragan Kuliner
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          height: 'calc(100vh - 120px)',
          p: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#1e293b',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#374151',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#4b5563',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#374151 #1e293b',
        }}
      >
        <GroupedMenu menu={MENU} />
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #374151',
          position: 'sticky',
          bottom: 0,
          bgcolor: '#1e293b',
          zIndex: 2,
        }}
      >
        <Button
          onClick={handleLogoutClick}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            color: "#cbd5e1",
            p: 1.5,
            borderRadius: 1,
            textTransform: "none",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "#EF4444",
              color: "white",
              transform: "scale(1.05)",
            },
            minHeight: "48px",
            touchAction: "manipulation",
          }}
          startIcon={<LogoutOutlined />}
        >
          Keluar
        </Button>
      </Box>
    </Box>
  );
};

export default Sidebar;
